import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useCompanyType } from "@/hooks/use-company-type";
import { useUser } from "@/contexts/UserContext";
import { useCompanyPreferences, resolveStaffNav } from "@/hooks/use-company-preferences";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useUserData } from "@/hooks/use-user-data";
import {
  Home,
  Package,
  ShoppingCart,
  Users,
  Settings,
  LogOut,
  X,
  ArrowRight,
  ArrowLeft,
  TreePine,
  BarChart2,
  ClipboardList,
  Shield,
  FlaskConical,
  Beaker,
  Zap,
  Trash2,
  UserRound,
  Truck,
  Bot,
  Handshake,
} from "lucide-react";

interface NavigationItem {
  title: string;
  path?: string;
  icon: React.ElementType;
  subItems?: NavigationItem[];
}

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

// Navigation items will be determined dynamically based on company type

interface MobileNavItemProps {
  item: NavigationItem;
  isActive: boolean;
  onItemClick: () => void;
}

const MobileNavItem: React.FC<MobileNavItemProps> = ({ item, isActive, onItemClick }) => {
  const { icon: Icon } = item;
  const navigate = useNavigate();

  if (!item.path) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('MobileNavItem clicked:', item.title, item.path);
    navigate(item.path);
    onItemClick();
  };

  return (
    <Button
      variant="ghost"
      onClick={handleClick}
      className={cn(
        "w-full justify-start h-12 px-4 text-left font-normal",
        "min-h-[44px] touch-manipulation", // Touch-friendly 44px minimum
        isActive 
          ? "bg-primary text-primary-foreground font-medium" 
          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      )}
    >
      <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
      <span className="truncate">{item.title}</span>
    </Button>
  );
};

export const MobileNavigation: React.FC<MobileNavigationProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();
  const { companyType, isTimberCompany, isLiquidCompany, isManufacturingCompany } = useCompanyType();
  const { user, isAdmin, isSystemAdmin, loading } = useUser();
  const { preferences } = useCompanyPreferences();

  // System admin access is decided by the backend (SYSTEM_ADMIN_EMAILS) and
  // arrives on the user profile, so the menu does not keep its own list.

  // Define different navigation items based on company type
  const getNavigationItems = (): NavigationItem[] => {
    const baseItems = [];
    
    // Add system admin dashboard for system admins
    if (isSystemAdmin) {
      baseItems.push({ title: "System Admin", path: "/system-admin", icon: Shield });
      baseItems.push({ title: "Referral Admin", path: "/admin/referrals", icon: Handshake });
    }
    
    // Only add admin dashboard for admin users
    if (isAdmin) {
      baseItems.push({ title: "Admin Dashboard", path: "/admin", icon: Shield });
    }

    if (isTimberCompany) {
      return [
        ...baseItems,
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Timber Inventory", path: "/timber", icon: TreePine },
        { title: "Production Top-Up", path: "/timber/production", icon: Package },
        { title: "Timber Analytics", path: "/timber-analytics", icon: BarChart2 },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Referrals", path: "/referrals/dashboard", icon: Handshake },
        { title: "Settings", path: "/settings", icon: Settings },
      ];
    } else if (isLiquidCompany || isManufacturingCompany) {
      return [
        ...baseItems,
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Inventory", path: "/inventory", icon: Package },
        { title: "Quick Sale", path: "/production/quick-sale", icon: Zap },
        { title: "Formulas", path: "/production/formulas", icon: FlaskConical },
        { title: "Production", path: "/production/run", icon: Beaker },
        { title: "Waste", path: "/waste", icon: Trash2 },
        { title: "Accounting", path: "/accounting", icon: BarChart2 },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "Credit Customers", path: "/credit-customers", icon: Users },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Referrals", path: "/referrals/dashboard", icon: Handshake },
        { title: "Settings", path: "/settings", icon: Settings },
      ];
    } else {
      return [
        ...baseItems,
        { title: "Dashboard", path: "/", icon: Home },
        { title: "Sales Orders", path: "/orders/sales", icon: ArrowRight },
        { title: "Website Orders", path: "/orders", icon: ClipboardList },
        { title: "Inventory", path: "/inventory", icon: Package },
        { title: "Suppliers", path: "/suppliers", icon: Truck },
        { title: "Customers", path: "/customers", icon: UserRound },
        { title: "Credit Customers", path: "/credit-customers", icon: Users },
        { title: "AI Agents", path: "/agents", icon: Bot },
        { title: "Referrals", path: "/referrals/dashboard", icon: Handshake },
        { title: "Settings", path: "/settings", icon: Settings },
      ];
    }
  };

  const navigationItems = loading ? [] : getNavigationItems();

  // Filter nav by the pages granted to this user: per-user list (staff_nav_users)
  // wins, then the general staff_nav; null means unrestricted (admin / no grants).
  const allowedNav = resolveStaffNav(preferences, user, isAdmin);
  const visibleNavItems = allowedNav === null
    ? isAdmin
      ? navigationItems
      : navigationItems.filter(item => item.path !== "/agents") // staff never see AI Agents (mirrors Sidebar)
    : navigationItems.filter(item => item.path && (allowedNav.includes(item.path) || item.path === "/settings")); // Settings always stays — it shows staff their own access



  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleItemClick = () => {
    console.log('MobileNavItem onItemClick called - closing menu');
    onClose();
  };

  const isActiveItem = (item: NavigationItem): boolean => {
    if (!item.path) return false;
    return item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
  };

  const handleLogout = () => {
    console.log('Mobile logout clicked');
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    navigate("/login");
    onClose();
  };

  return (
    <>


      {/* Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 lg:hidden",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Mobile Navigation Drawer */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-sidebar text-sidebar-foreground border-r border-sidebar-border z-50",
          "transform transition-transform duration-300 ease-in-out lg:hidden",
          "flex flex-col shadow-float",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-sidebar-border bg-sidebar/95 backdrop-blur-sm">
          <div className="flex items-center">
            {/* White chip keeps the wordmark legible on the navy drawer */}
            <img src="/stockwise-logo.png" alt="StockWise Logo" className="h-10 w-auto bg-white rounded-md px-1.5 py-1" />
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10 min-h-[44px] min-w-[44px] touch-manipulation"
            aria-label="Close navigation menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-1 px-2" role="navigation">
            {visibleNavItems.map((item) => (
              <MobileNavItem
                key={item.title}
                item={item}
                isActive={isActiveItem(item)}
                onItemClick={handleItemClick}
              />
            ))}
          </nav>
        </div>

        {/* Footer with Logout */}
        <div className="p-4 border-t border-sidebar-border bg-sidebar/95 backdrop-blur-sm">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start h-12 px-4 text-left font-normal min-h-[44px] touch-manipulation text-sidebar-foreground hover:bg-destructive/15"
          >
            <LogOut className="h-5 w-5 mr-3 flex-shrink-0" />
            <span>Logout</span>
          </Button>
        </div>
      </div>
    </>
  );
};